import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/** Keep the menu open briefly on leave so opening under the cursor (or a 1px gap) doesn't instantly close it. */
const HOVER_LEAVE_DELAY_MS = 120;

/** Touch/tablet: hover-free navigation uses click to open/close instead of pointerenter/leave. */
const tapMenuQuery = '(hover: none), (pointer: coarse)';

/** @type {MediaQueryList | null} */
let tapMenuQueryMedia = null;

function getTapMenuQueryMedia() {
  if (!tapMenuQueryMedia) {
    tapMenuQueryMedia = window.matchMedia(tapMenuQuery);
  }
  return tapMenuQueryMedia;
}

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {

  /** @type {MutationObserver | null} */
  #submenuMutationObserver = null;

  /** @type {MutationObserver | null} */
  #promoMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverLeaveTimer;

  connectedCallback() {
    super.connectedCallback();

    this.#placeMegaMenuPanels();
    onDocumentLoaded(() => {
      this.#placeMegaMenuPanels();
      this.#preloadImages();
    });

    // Section hydration morphs this menu after first paint and would drop injected panels.
    this.#promoMutationObserver = new MutationObserver(
      debounce(() => {
        this.#placeMegaMenuPanels();
      }, 50)
    );
    this.#promoMutationObserver.observe(this, { childList: true, subtree: true });

    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    getTapMenuQueryMedia().addEventListener('change', this.#handleTapModeChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    document.removeEventListener('pointerdown', this.#onOutsidePointerDown, true);
    getTapMenuQueryMedia().removeEventListener('change', this.#handleTapModeChange);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    this.#promoMutationObserver?.disconnect();
    this.#promoMutationObserver = null;
    this.#setMegaMenuOverlay(false);
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    clearTimeout(this.#hoverLeaveTimer);
    this.#hoverLeaveTimer = undefined;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    const submenu = findSubmenu(this.#state.activeItem);
    if (submenu && this.#state.activeItem) {
      this.#positionSubmenu(submenu, this.#state.activeItem);
      this.#fitSubmenuInViewport(submenu);
      this.#syncActiveSubmenuHeight();
    }
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Whether interactions should use tap-to-toggle instead of hover.
   * @param {Event} [event]
   * @returns {boolean}
   */
  #usesTapMenu(event) {
    if (event instanceof PointerEvent && event.pointerType === 'touch') return true;
    return getTapMenuQueryMedia().matches;
  }

  #handleTapModeChange = () => {
    if (!getTapMenuQueryMedia().matches) {
      document.removeEventListener('pointerdown', this.#onOutsidePointerDown, true);
    }
  };

  /**
   * Open, close, or switch mega menus via tap (tablet/touch).
   * Uses pointerdown because iOS often skips click on the first touch.
   * @param {PointerEvent} event
   */
  handleMenuPointerDown = (event) => {
    if (!(event instanceof PointerEvent) || !this.#usesTapMenu(event)) return;
    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item?.hasAttribute('aria-haspopup')) return;

    event.preventDefault();

    if (item === this.#state.activeItem) {
      this.#deactivate(item);
      return;
    }

    this.activate(event);
  };

  /**
   * Close the open mega menu when tapping outside the nav.
   * @param {PointerEvent} event
   */
  #onOutsidePointerDown = (event) => {
    if (!(event.target instanceof Element) || !this.#state.activeItem) return;
    if (this.contains(event.target)) return;

    this.#deactivate();
  };

  #bindOutsideDismiss(event) {
    if (!this.#usesTapMenu(event)) return;
    document.addEventListener('pointerdown', this.#onOutsidePointerDown, true);
  }

  #unbindOutsideDismiss() {
    document.removeEventListener('pointerdown', this.#onOutsidePointerDown, true);
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    if (this.#usesTapMenu(event) && event instanceof PointerEvent && event.type === 'pointerenter') {
      return;
    }

    clearTimeout(this.#hoverLeaveTimer);
    this.#hoverLeaveTimer = undefined;

    if (event instanceof PointerEvent) {
      this.#lastPointer.x = event.clientX;
      this.#lastPointer.y = event.clientY;
    }

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else if (event instanceof PointerEvent && event.pointerType === 'touch') {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';
      this.#setMegaMenuOverlay(true);
      this.#positionSubmenu(submenu, item);
      this.#fitSubmenuInViewport(submenu);

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            this.#fitSubmenuInViewport(submenu);
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    // Re-measure after fit so height accounts for any wrapping from a narrower panel
    if (submenu && isDefaultSlot) {
      this.#positionSubmenu(submenu, item);
      this.#fitSubmenuInViewport(submenu);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
    this.#bindOutsideDismiss(event);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (this.#usesTapMenu(event)) return;

    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const related = event.relatedTarget;
    const isMovingWithinMenu = related instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      related instanceof Node && (event.type === 'blur' || event.type === 'pointerleave') && menu?.contains(related);
    const isMovingToOverflowMenu =
      related instanceof Node && related.parentElement?.matches('[slot="overflow"]');
    const isMovingToMenuItem =
      related instanceof Element && Boolean(related.closest('.menu-list__list-item')?.contains(this.#state.activeItem));

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu || isMovingToMenuItem) {
      if (this.#state.activeItem && (isMovingWithinMenu || isMovingToSubmenu)) {
        // Keep submenu open; pointer tracking can stop while resting on the panel
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    clearTimeout(this.#hoverLeaveTimer);
    this.#hoverLeaveTimer = setTimeout(() => {
      this.#hoverLeaveTimer = undefined;

      // If the pointer is still over this item or its submenu, keep it open
      // (opening the panel under the cursor can fire a spurious leave).
      const { x, y } = this.#lastPointer;
      const underPointer = document.elementFromPoint(x, y);
      if (underPointer && this.#state.activeItem) {
        const listItem = this.#state.activeItem.closest('.menu-list__list-item');
        if (listItem?.contains(underPointer)) return;
      }

      this.#deactivate();
    }, HOVER_LEAVE_DELAY_MS);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    clearTimeout(this.#hoverLeaveTimer);
    this.#hoverLeaveTimer = undefined;
    this.#unbindOutsideDismiss();

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    this.#setMegaMenuOverlay(false);

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
      this.#resetSubmenuViewportFit(submenu);
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #placeMegaMenuPanels = () => {
    this.#placeMegaMenuPromos();
    this.#placeMegaMenuContact();
    this.#placeMegaMenuLearning();
  };

  /**
   * Theme-block promo images can't be read via section.blocks / block.blocks from a
   * static menu (settings come back empty as type "@theme"). Each Mega menu image
   * block renders a <template> source; clone those into the matching submenu grid.
   * Up to 3 promos are placed per menu handle (theme editor order).
   */
  #placeMegaMenuPromos = () => {
    const sources = document.querySelectorAll('template.mega-menu-promo-source[data-mega-menu-handle]');
    if (!sources.length) return;

    const maxPromosPerMenu = 3;
    /** @type {Map<string, Element[]>} */
    const sourcesByHandle = new Map();

    sources.forEach((source) => {
      const handle = source.getAttribute('data-mega-menu-handle');
      if (!handle) return;

      const handleSources = sourcesByHandle.get(handle) ?? [];
      if (handleSources.length >= maxPromosPerMenu) return;

      handleSources.push(source);
      sourcesByHandle.set(handle, handleSources);
    });

    let placed = false;

    sourcesByHandle.forEach((handleSources, handle) => {
      const grids = document.querySelectorAll(
        `.mega-menu__grid[data-menu-handle="${CSS.escape(handle)}"], .mega-menu__grid[data-menu-title-handle="${CSS.escape(handle)}"]`
      );

      grids.forEach((grid) => {
        if (grid.querySelector(':scope > .mega-menu__promos, :scope > .mega-menu__promo')) return;

        const wrapper = document.createElement('div');
        wrapper.className = 'mega-menu__promos';
        wrapper.setAttribute('data-promo-count', String(handleSources.length));

        handleSources.forEach((source) => {
          const promo = source.content.firstElementChild?.cloneNode(true);
          if (!(promo instanceof HTMLElement)) return;

          const link = promo.querySelector('a.mega-menu__promo-card');
          if (link instanceof HTMLAnchorElement && link.hasAttribute('data-mega-menu-default-link')) {
            const menuUrl = grid.getAttribute('data-menu-url');
            if (menuUrl) link.href = menuUrl;
            link.removeAttribute('data-mega-menu-default-link');
          }

          wrapper.appendChild(promo);

          const image = promo.querySelector('img');
          if (image instanceof HTMLImageElement) {
            image.removeAttribute('loading');
            if (!image.complete) {
              image.addEventListener('load', () => this.#syncActiveSubmenuHeight(), { once: true });
            }
          }
        });

        if (!wrapper.children.length) return;

        grid.classList.add('mega-menu__grid--has-promo');
        grid.classList.add(`mega-menu__grid--has-promo-${wrapper.children.length}`);
        grid.style.setProperty('--menu-columns-desktop', '6');
        grid.style.setProperty('--menu-columns-tablet', '4');
        grid.appendChild(wrapper);
        placed = true;
      });
    });

    if (placed) this.#syncActiveSubmenuHeight();
  };

  /**
   * Contact panel templates replace the normal mega-menu list for a matched handle.
   */
  #placeMegaMenuContact = () => {
    const sources = document.querySelectorAll('template.mega-menu-contact-source[data-mega-menu-handle]');
    if (!sources.length) return;

    let placed = false;

    sources.forEach((source) => {
      const handle = source.getAttribute('data-mega-menu-handle');
      if (!handle) return;

      this.#ensureContactSubmenu(handle);

      const grids = document.querySelectorAll(
        `.mega-menu__grid[data-menu-handle="${CSS.escape(handle)}"], .mega-menu__grid[data-menu-title-handle="${CSS.escape(handle)}"]`
      );

      grids.forEach((grid) => {
        if (grid.querySelector(':scope > [data-mega-menu-contact]')) return;

        const panel = source.content.firstElementChild?.cloneNode(true);
        if (!(panel instanceof HTMLElement)) return;

        const expertLink = panel.querySelector('a.mega-menu-contact__expert-link');
        if (expertLink instanceof HTMLAnchorElement && expertLink.hasAttribute('data-mega-menu-default-link')) {
          const menuUrl = grid.getAttribute('data-menu-url');
          if (menuUrl) expertLink.href = menuUrl;
          expertLink.removeAttribute('data-mega-menu-default-link');
        }

        grid.classList.add('mega-menu__grid--contact');
        grid.replaceChildren(panel);
        placed = true;
      });
    });

    if (placed) this.#syncActiveSubmenuHeight();
  };

  /**
   * Ensure a top-level link matching the contact panel handle has caret + submenu shell.
   * @param {string} handle
   */
  #ensureContactSubmenu = (handle) => {
    const links = this.querySelectorAll(
      `a.menu-list__link[data-menu-handle="${CSS.escape(handle)}"], a.menu-list__link[data-menu-title-handle="${CSS.escape(handle)}"]`
    );

    links.forEach((link, index) => {
      if (!(link instanceof HTMLAnchorElement)) return;

      const item = link.closest('.menu-list__list-item');
      if (!(item instanceof HTMLElement)) return;

      let submenu = item.querySelector(':scope > .menu-list__submenu');
      if (!submenu) {
        const submenuId = `submenu-contact-${handle}-${index}`;
        submenu = document.createElement('div');
        submenu.className = 'menu-list__submenu';
        submenu.setAttribute('ref', 'submenu[]');
        submenu.innerHTML = `
          <div id="${submenuId}" class="menu-list__submenu-inner">
            <div class="mega-menu section section--full-width-margin">
              <div
                class="mega-menu__grid mega-menu__grid--contact-shell"
                data-menu-handle="${handle}"
                data-menu-title-handle="${handle}"
                data-menu-url="${link.href}"
              ></div>
            </div>
          </div>
        `;
        item.appendChild(submenu);

        link.setAttribute('aria-controls', submenuId);
        link.setAttribute('aria-haspopup', 'true');
        link.setAttribute('aria-expanded', 'false');

        if (!link.querySelector('.menu-list__link-icon')) {
          const icon = document.createElement('span');
          icon.className = 'menu-list__link-icon';
          icon.setAttribute('aria-hidden', 'true');
          icon.innerHTML =
            link.closest('header-menu')?.querySelector('.menu-list__link-icon')?.innerHTML ||
            '<svg viewBox="0 0 12 8" width="10" height="6" aria-hidden="true"><path d="M1 1.5 6 6.5 11 1.5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';
          link.appendChild(icon);
        }
      }
    });
  };

  /**
   * Learning Hub panel templates replace the normal mega-menu list for a matched handle.
   */
  #placeMegaMenuLearning = () => {
    const sources = document.querySelectorAll('template.mega-menu-learning-source[data-mega-menu-handle]');
    if (!sources.length) return;

    let placed = false;

    sources.forEach((source) => {
      const handle = source.getAttribute('data-mega-menu-handle');
      if (!handle) return;

      this.#ensureLearningSubmenu(handle);

      const grids = document.querySelectorAll(
        `.mega-menu__grid[data-menu-handle="${CSS.escape(handle)}"], .mega-menu__grid[data-menu-title-handle="${CSS.escape(handle)}"]`
      );

      grids.forEach((grid) => {
        if (grid.querySelector(':scope > [data-mega-menu-learning]')) return;

        const panel = source.content.firstElementChild?.cloneNode(true);
        if (!(panel instanceof HTMLElement)) return;

        const ctaLink = panel.querySelector('a.mega-menu-learning__cta');
        if (ctaLink instanceof HTMLAnchorElement && ctaLink.hasAttribute('data-mega-menu-default-link')) {
          const menuUrl = grid.getAttribute('data-menu-url');
          if (menuUrl) ctaLink.href = menuUrl;
          ctaLink.removeAttribute('data-mega-menu-default-link');
        }

        grid.classList.add('mega-menu__grid--learning');
        grid.replaceChildren(panel);
        placed = true;
      });
    });

    if (placed) this.#syncActiveSubmenuHeight();
  };

  /**
   * Ensure a top-level link matching the learning panel handle has caret + submenu shell.
   * @param {string} handle
   */
  #ensureLearningSubmenu = (handle) => {
    const links = this.querySelectorAll(
      `a.menu-list__link[data-menu-handle="${CSS.escape(handle)}"], a.menu-list__link[data-menu-title-handle="${CSS.escape(handle)}"]`
    );

    links.forEach((link, index) => {
      if (!(link instanceof HTMLAnchorElement)) return;

      const item = link.closest('.menu-list__list-item');
      if (!(item instanceof HTMLElement)) return;

      item.classList.add('menu-list__list-item--learning');
      link.classList.add('menu-list__link--learning');

      // Keep Learning Hub as a plain text link (no book icon)
      link.querySelectorAll('.menu-learning-icon').forEach((icon) => icon.remove());

      let submenu = item.querySelector(':scope > .menu-list__submenu');
      if (!submenu) {
        const submenuId = `submenu-learning-${handle}-${index}`;
        submenu = document.createElement('div');
        submenu.className = 'menu-list__submenu';
        submenu.setAttribute('ref', 'submenu[]');
        submenu.innerHTML = `
          <div id="${submenuId}" class="menu-list__submenu-inner">
            <div class="mega-menu section section--full-width-margin">
              <div
                class="mega-menu__grid mega-menu__grid--learning-shell"
                data-menu-handle="${handle}"
                data-menu-title-handle="${handle}"
                data-menu-url="${link.href}"
              ></div>
            </div>
          </div>
        `;
        item.appendChild(submenu);

        link.setAttribute('aria-controls', submenuId);
        link.setAttribute('aria-haspopup', 'true');
        link.setAttribute('aria-expanded', 'false');

        if (!link.querySelector('.menu-list__link-icon')) {
          const icon = document.createElement('span');
          icon.className = 'menu-list__link-icon';
          icon.setAttribute('aria-hidden', 'true');
          icon.innerHTML =
            link.closest('header-menu')?.querySelector('.menu-list__link-icon')?.innerHTML ||
            '<svg viewBox="0 0 12 8" width="10" height="6" aria-hidden="true"><path d="M1 1.5 6 6.5 11 1.5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';
          link.appendChild(icon);
        }
      }
    });
  };

  #setMegaMenuOverlay = (open) => {
    document.body.classList.toggle('mega-menu-open', open);

    const headerSection = document.querySelector('.header-section');
    if (headerSection instanceof HTMLElement) {
      if (open) {
        headerSection.style.setProperty('z-index', 'calc(var(--layer-menu-drawer) + 2)');
      } else {
        headerSection.style.removeProperty('z-index');
      }
    }

    let overlay = document.querySelector('.header__mega-menu-overlay');
    if (!(overlay instanceof HTMLElement)) {
      overlay = document.createElement('div');
      overlay.className = 'header__mega-menu-overlay';
      overlay.setAttribute('aria-hidden', 'true');
      document.body.appendChild(overlay);
    } else if (overlay.parentElement !== document.body) {
      document.body.appendChild(overlay);
    }
  };

  #syncActiveSubmenuHeight = () => {
    const submenu = this.querySelector('.menu-list__submenu[data-active]');
    if (!(submenu instanceof HTMLElement) || submenu.offsetHeight <= 0) return;

    const headerVisibleHeight = this.#getHeaderVisibleHeight();
    this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
    this.#setFullOpenHeaderHeight(submenu.offsetHeight, headerVisibleHeight);
  };

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }

  /**
   * Position submenu below the full wrapped nav list, not just the hovered row.
   * @param {HTMLElement} submenu
   * @param {HTMLElement} item
   */
  #positionSubmenu(submenu, item) {
    if (!(submenu instanceof HTMLElement) || !(item instanceof HTMLElement)) return;
    if (submenu.closest('[slot="overflow"]')) return;

    const listItem = item.closest('.menu-list__list-item');
    const menuList = item.closest('.menu-list__list');
    if (!(listItem instanceof HTMLElement) || !(menuList instanceof HTMLElement)) return;

    const itemRect = listItem.getBoundingClientRect();
    const menuListRect = menuList.getBoundingClientRect();
    const headerRow = this.closest('.header__row--bottom') ?? this.closest('.header__row');
    const headerPadding =
      headerRow instanceof HTMLElement
        ? Number.parseFloat(getComputedStyle(headerRow).getPropertyValue('--header-padding')) || 0
        : 0;

    const topOffset = menuListRect.bottom - itemRect.top + headerPadding;
    submenu.style.setProperty('--submenu-top', `${topOffset}px`);
  }

  /**
   * Keep floating mega menus inside the viewport (shift left + clamp max-width).
   * @param {HTMLElement} submenu
   */
  #fitSubmenuInViewport(submenu) {
    if (!(submenu instanceof HTMLElement)) return;
    if (submenu.closest('[slot="overflow"]')) return;

    const margin = 16;
    const maxWidth = Math.max(240, window.innerWidth - margin * 2);

    submenu.style.left = '0px';
    submenu.style.maxWidth = `${maxWidth}px`;

    const inner = submenu.querySelector(':scope > .menu-list__submenu-inner');
    if (inner instanceof HTMLElement) {
      inner.style.maxWidth = `${maxWidth}px`;
    }

    // Measure after applying max-width so content can wrap/shrink first
    const rect = submenu.getBoundingClientRect();
    const overflowRight = rect.right - (window.innerWidth - margin);

    if (overflowRight > 0) {
      submenu.style.left = `${-overflowRight}px`;
    }

    const fittedRect = submenu.getBoundingClientRect();
    if (fittedRect.left < margin) {
      const currentLeft = Number.parseFloat(submenu.style.left || '0') || 0;
      submenu.style.left = `${currentLeft + (margin - fittedRect.left)}px`;
    }
  }

  /**
   * Clear inline positioning applied by #fitSubmenuInViewport.
   * @param {HTMLElement} submenu
   */
  #resetSubmenuViewportFit(submenu) {
    if (!(submenu instanceof HTMLElement)) return;

    submenu.style.removeProperty('left');
    submenu.style.removeProperty('max-width');
    submenu.style.removeProperty('--submenu-top');

    const inner = submenu.querySelector(':scope > .menu-list__submenu-inner');
    if (inner instanceof HTMLElement) {
      inner.style.removeProperty('max-width');
    }
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const listItem = element.closest('.menu-list__list-item');
  if (!(listItem instanceof HTMLElement)) return null;

  const menuitem = listItem.querySelector('[ref="menuitem"]');
  return menuitem instanceof HTMLElement ? menuitem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
